"use strict";

define('rs/form/factory',['rs/form/form.controller', 'rs/form/formdesigner.controller', 'rs/form/formdiff/formdiff.controller', 'rs/form/formpdf/formpdf.controller', 'rs/models/template', 'rs/helper', 'jquery', 'underscore', 'rs/services/document.service', 'rs/models/document', 'rs/models/study', 'rs/models/document.lock', 'rs/models/session', 'rs/models/assignment.builder', 'rs/models/assignments', 'rs/errors'], function (FormController, FormDesignerController, FormDiffController, FormPDFController, TemplateModel, helper, $, _, DocumentService, Document, Study, DocumentLock, Session, AssignmentBuilder, Assignments, RSError) {
  var FormFactory = {
    createFormViewer: function createFormViewer(documentId, userId, appController) {
      var data = {
        templateModel: null,
        task: null,
        documentModel: null,
        template: null,
        commentCounts: null,
        noSavesInAnswers: null,
        locked: false,
        formObjectData: null,
        documentLock: null,
        readOnly: undefined
      };
      var documentService = new DocumentService({
          documentId: documentId
        }),
        workflowService = null;
      return documentService.load('document', 'template', 'permissions', 'answers', 'workflowService.tasks', 'assignments').spread(function (doc, template, permissions) {
        workflowService = documentService.getWorkflowService();
        data.workflowService = workflowService;
        data.documentModel = doc;
        data.readOnly = doc.readOnly;
        data.templateModel = template;
        if (template.getReadOnly()) {
          return FormFactory.requestJSONFromTemplate(template);
        }
        throw RSError.InvalidSubmissionTemplate("This document cannot be loaded because the template has not been published.");
      }).then(function (json) {
        data.template = json;
        var answerData = documentService.getAnswers();
        data.formObjectData = _.indexBy(answerData, 'id');
        data.noSavesInAnswers = FormFactory.isAnswerDataEmpty(answerData || [], json);
        var shouldGetAssignments = data.noSavesInAnswers && documentService.getPermissions().canWrite;
        return Assignments.requestAll({
          studyId: data.documentModel.studyId,
          lazyFetch: !shouldGetAssignments
        });
      }).then(function (assignments) {
        data.assignments = assignments;
        return data.documentModel.requestCommentCounts();
      }).then(function (counts) {
        data.commentCounts = counts;
        data.documentLock = new DocumentLock(documentId, userId);
        return data.documentLock.setup();
      }).then(function () {
        return data.documentModel.requestCommentDigest();
      }).then(function (comments) {
        data.comments = comments || [];
        Session.triggerActivity('documentId', data.documentModel.getId());
        return Session.doHeartbeat();
      }).then(function () {
        var formController = new FormController(data);
        formController.listenTo(data.documentLock, 'updatelist', formController.locksDidUpdate);
        formController.listenTo(data.documentLock, 'receivedLock', function (lock) {
          Session.triggerActivity('templateObjectId', lock.templateObjectId);
        });
        formController.listenTo(data.documentLock, 'releaseLock', function () {
          Session.resetActivity('templateObjectId');
        });
        formController.once('unload', function () {
          Session.resetActivity('templateObjectId');
          Session.resetActivity('documentId');
        });
        formController.listenTo(Session, 'documentLockChangeDate', function () {
          data.documentLock.updateLockList();
        });
        return formController;
      });
    },
    createFormDiffPair: function createFormDiffPair(documentId, userId) {
      var data = {
        templateModel: null,
        task: null,
        documentModel: null,
        template: null,
        commentCounts: null,
        locked: false,
        readOnly: true,
        formObjectData: null
      };
      return Document.requestDocument(documentId).then(function (doc) {
        data.documentModel = doc;
        return data.documentModel.requestTemplate();
      }).then(function (template) {
        data.templateModel = template;
        if (data.templateModel.getReadOnly()) {
          return FormFactory.requestJSONFromTemplate(data.templateModel);
        }
        throw RSError.InvalidSubmissionTemplate("This document cannot be loaded because the template has not been published.");
      }).then(function (json) {
        data.template = json;
        return data.documentModel.requestCommentCounts();
      }).then(function (counts) {
        data.commentCounts = counts;
        var fc1 = new FormDiffController(data),
          fc2 = new FormDiffController(data);
        return Promise.resolve([fc1, fc2]);
      });
    },
    createFormPDFViewer: function createFormPDFViewer(documentId, userId, appController) {
      var data = {
        templateModel: null,
        task: null,
        documentModel: null,
        template: null,
        commentCounts: null,
        noSavesInAnswers: null,
        locked: false,
        readOnly: true,
        formObjectData: null
      };
      var documentService = new DocumentService({
          documentId: documentId
        }),
        workflowService = null;
      return documentService.load('document', 'template', 'permissions', 'answers', 'workflowService.tasks', 'assignments').spread(function (doc, template) {
        data.documentModel = doc;
        workflowService = documentService.getWorkflowService();
        data.workflowService = workflowService;
        data.templateModel = template;
        if (data.templateModel.getReadOnly()) {
          return FormFactory.requestJSONFromTemplate(data.templateModel);
        }
        throw RSError.InvalidSubmissionTemplate("This document cannot be loaded because the template has not been published.");
      }).then(function (json) {
        data.template = json;
        var answerData = documentService.getAnswers();
        data.formObjectData = _.indexBy(answerData, 'id');
        data.noSavesInAnswers = FormFactory.isAnswerDataEmpty(answerData || [], json);
        return Assignments.requestAll({
          studyId: data.documentModel.studyId
        });
      }).then(function (assignments) {
        data.assignments = assignments;
        var formPDFController = new FormPDFController(data);
        return formPDFController;
      });
    },
    createFormEditor: function createFormEditor(templateId, revisionNumber, preview) {
      var data = {
        templateModel: null,
        assignmentTypes: null,
        template: null,
        readOnly: preview ? false : true
      };
      return TemplateModel.requestFormTemplate(templateId, revisionNumber).then(function (templateModel) {
        data.templateModel = templateModel;
        data.templateModel.preview = !!preview;
        if (!preview) {
          return data.templateModel.requestLock();
        }
      }).then(function () {
        return FormFactory.requestJSONFromTemplate(data.templateModel);
      }).then(function (json) {
        data.template = json;
        if (!preview) {
          Session.triggerActivity('templateId', data.templateModel.getId());
          return Session.doHeartbeat();
        }
      }).then(function () {
        return AssignmentBuilder.requestAssignmentTypes();
      }).then(function (types) {
        data.assignmentTypes = types || [];
        if (preview) {
          return new FormController(data);
        }
        return new FormDesignerController(data);
      });
    },
    parseTemplateJSONString: function parseTemplateJSONString(jsonString) {
      try {
        return $.parseJSON(jsonString) || {};
      } catch (e) {
        return {};
      }
    },
    requestJSONFromTemplate: function requestJSONFromTemplate(templateModel) {
      var json,
        that = this;
      if (_.isArray(templateModel.json)) {
        json = _.map(templateModel.json, function (m) {
          return that.parseTemplateJSONString(m);
        });
        json[0].children = json[0].children || [];
        json[0].attachments = json[0].attachments || [];
        while (json.length > 1) {
          if (_.isArray(json[1].children)) {
            json[0].children = json[0].children.concat(json[1].children);
          }
          if (_.isArray(json[1].attachments)) {
            json[0].attachments = json[0].attachments.concat(json[1].attachments);
          }
          json.splice(1, 1);
        }
        json = json[0];
      } else {
        json = this.parseTemplateJSONString(templateModel.json);
      }
      return json;
    },
    isAnswerDataEmpty: function isAnswerDataEmpty(data, template) {
      var children = template.children,
        answer = null;
      for (var i = 0, len = data.length; i < len; i++) {
        answer = data[i];
        if (answer.value != 'viewed' || !_.findWhere(children, {
          dataId: answer.id
        })) {
          return false;
        }
      }
      return true;
    }
  };
  return FormFactory;
});

